/**
 * Custom hook for managing market prices
 * Provides efficient, scalable price fetching and updates
 * 
 * Features:
 * - Fetches prices for multiple markets
 * - Updates prices periodically without continuous requests
 * - Caches prices per market (not global cache)
 * - Provides price updates via callback
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { queryContract } from '../Components/functions/Contract';
import { getContractAddresses } from '../config/network';
import { getNibiruSDK } from '../config/network';
import { parseMarketIndex } from '../utils/marketUtils';

const nibiru = getNibiruSDK();

// Per-market price cache (not global)
const marketPriceCache = new Map();

// Price update interval (30 seconds)
const PRICE_UPDATE_INTERVAL = 30000;

/**
 * Fetch price for a specific market
 * @param {string|number} marketIndex - Market index
 * @returns {Promise<number|null>} Current price or null
 */
const fetchMarketPrice = async (marketIndex) => {
  try {
    // Parse market index
    let parsedIndex = marketIndex;
    if (typeof marketIndex === 'string') {
      parsedIndex = parseMarketIndex(marketIndex);
      if (parsedIndex === null) {
        console.warn('Could not parse market_index:', marketIndex);
        return null;
      }
    }

    // Check cache first (per-market cache)
    const cacheKey = parsedIndex;
    const cached = marketPriceCache.get(cacheKey);
    const now = Date.now();
    
    // Use cache if fresh (60 second TTL per market - optimized for speed)
    // This allows instant display of cached prices while fresh ones load in background
    if (cached && (now - cached.timestamp < 60000)) {
      return cached.price;
    }

    // Fetch from contract using get_perp_prices
    // Format market_index correctly
    let formattedMarketIndex;
    if (typeof marketIndex === 'string') {
      // If already formatted as "MarketIndex(0)", use as is
      if (marketIndex.includes('MarketIndex')) {
        formattedMarketIndex = marketIndex;
      } else {
        // If it's just a number string, format it
        formattedMarketIndex = `MarketIndex(${marketIndex})`;
      }
    } else {
      formattedMarketIndex = `MarketIndex(${marketIndex})`;
    }

    const message = {
      get_perp_prices: {
        market_index: formattedMarketIndex,
        collateral_index: process.env.REACT_APP_PERP_TOKEN_INDEXES_COLLATERAL || 'TokenIndex(4)',
      },
    };

    const contracts = getContractAddresses();
    const result = await queryContract(
      contracts.perp,
      message,
      nibiru.endptTm
    );

    if (result?.market?.base_price_usd) {
      const price = parseFloat(result.market.base_price_usd);
      
      // Update per-market cache
      marketPriceCache.set(cacheKey, {
        price,
        timestamp: now,
      });
      
      return price;
    }

    // Fallback to cached price if available (even if expired)
    if (cached) {
      return cached.price;
    }

    return null;
  } catch (error) {
    console.error('Error fetching market price:', error);
    
    // Return cached price on error if available
    const cacheKey = typeof marketIndex === 'string' 
      ? parseMarketIndex(marketIndex) 
      : marketIndex;
    const cached = marketPriceCache.get(cacheKey);
    return cached ? cached.price : null;
  }
};

/**
 * Custom hook for managing market prices
 * @param {Array<string|number>} marketIndexes - Array of market indexes to track
 * @param {number} updateInterval - Update interval in ms (default: 30000)
 * @returns {Object} { prices, isLoading, refreshPrice, refreshAllPrices }
 */
export const useMarketPrices = (marketIndexes = [], updateInterval = PRICE_UPDATE_INTERVAL) => {
  // Initialize prices from cache synchronously so values appear instantly
  const [prices, setPrices] = useState(() => {
    const initial = new Map();
    marketIndexes.forEach(marketIndex => {
      let parsedIndex = marketIndex;
      if (typeof marketIndex === 'string') {
        parsedIndex = parseMarketIndex(marketIndex);
        if (parsedIndex === null) return;
      }
      const cached = marketPriceCache.get(parsedIndex);
      if (cached) {
        initial.set(marketIndex, cached.price);
      }
    });
    return initial;
  });
  const [isLoading, setIsLoading] = useState(false);
  const intervalRef = useRef(null);
  const marketIndexesRef = useRef(marketIndexes);
  // Initialize to null so the first effect always triggers a fetch
  const previousMarketIndexesRef = useRef(null);

  // Update ref when marketIndexes change
  useEffect(() => {
    marketIndexesRef.current = marketIndexes;
  }, [marketIndexes]);

  // Fetch price for a single market
  const refreshPrice = useCallback(async (marketIndex) => {
    const price = await fetchMarketPrice(marketIndex);
    if (price !== null) {
      setPrices(prev => {
        const newPrices = new Map(prev);
        newPrices.set(marketIndex, price);
        return newPrices;
      });
    }
    return price;
  }, []);

  // Fetch prices for all tracked markets
  const refreshAllPrices = useCallback(async () => {
    if (marketIndexesRef.current.length === 0) {
      return;
    }

    // First, immediately populate with cached prices (stale-while-revalidate pattern)
    // This gives instant results while fresh prices load in background
    const cachedPrices = new Map();
    marketIndexesRef.current.forEach(marketIndex => {
      let parsedIndex = marketIndex;
      if (typeof marketIndex === 'string') {
        parsedIndex = parseMarketIndex(marketIndex);
        if (parsedIndex === null) return;
      }
      const cached = marketPriceCache.get(parsedIndex);
      if (cached) {
        cachedPrices.set(marketIndex, cached.price);
      }
    });
    
    // Update state immediately with cached prices if we have any
    if (cachedPrices.size > 0) {
      setPrices(prev => {
        const newPrices = new Map(prev);
        cachedPrices.forEach((price, marketIndex) => {
            newPrices.set(marketIndex, price);
        });
        return newPrices;
      });
    }

    setIsLoading(true);
    try {
      // Fetch all prices in parallel and update state progressively as each arrives
      // This provides instant feedback as prices load
      const pricePromises = marketIndexesRef.current.map(async (marketIndex) => {
        try {
          const price = await fetchMarketPrice(marketIndex);
          // Update state immediately when each price arrives (progressive updates)
          if (price !== null) {
            setPrices(prev => {
              const newPrices = new Map(prev);
              newPrices.set(marketIndex, price);
              return newPrices;
            });
          }
          return { marketIndex, price, success: true };
        } catch (error) {
          console.error(`Error fetching price for market ${marketIndex}:`, error);
          return { marketIndex, price: null, success: false };
        }
      });
      
      // Wait for all to complete (but state updates happen progressively above)
      await Promise.allSettled(pricePromises);
    } catch (error) {
      console.error('Error refreshing all prices:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial fetch and refetch when marketIndexes change
  useEffect(() => {
    const currentMarketIndexesStr = JSON.stringify(marketIndexes);
    
    // Only refetch if marketIndexes actually changed
    if (previousMarketIndexesRef.current !== currentMarketIndexesStr) {
      previousMarketIndexesRef.current = currentMarketIndexesStr;
      
    if (marketIndexes.length > 0) {
        // Start fetching immediately, don't wait
      refreshAllPrices();
      } else {
        // Clear prices when no markets to track
        setPrices(new Map());
        setIsLoading(false);
      }
    }
  }, [marketIndexes, refreshAllPrices]); // Refetch when marketIndexes change

  // Set up periodic updates
  useEffect(() => {
    if (marketIndexes.length === 0) {
      return;
    }

    // Clear existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Set up new interval
    intervalRef.current = setInterval(() => {
      refreshAllPrices();
    }, updateInterval);

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [marketIndexes.length, updateInterval, refreshAllPrices]);

  // Get price for a specific market
  const getPrice = useCallback((marketIndex) => {
    return prices.get(marketIndex) || null;
  }, [prices]);

  return {
    prices,
    isLoading,
    refreshPrice,
    refreshAllPrices,
    getPrice,
  };
};

/**
 * Hook for getting price of a single market
 * @param {string|number} marketIndex - Market index
 * @param {number} updateInterval - Update interval in ms
 * @returns {number|null} Current price or null
 */
export const useMarketPrice = (marketIndex, updateInterval = PRICE_UPDATE_INTERVAL) => {
  // Initialize from cache synchronously for instant display
  const [price, setPrice] = useState(() => {
    if (!marketIndex) return null;
    let parsedIndex = marketIndex;
    if (typeof marketIndex === 'string') {
      parsedIndex = parseMarketIndex(marketIndex);
      if (parsedIndex === null) return null;
    }
    const cached = marketPriceCache.get(parsedIndex);
    return cached ? cached.price : null;
  });
  const [isLoading, setIsLoading] = useState(false);
  const intervalRef = useRef(null);

  const refreshPrice = useCallback(async () => {
    if (!marketIndex) {
      return;
    }

    setIsLoading(true);
    try {
      const fetchedPrice = await fetchMarketPrice(marketIndex);
      if (fetchedPrice !== null) {
        setPrice(fetchedPrice);
      }
    } catch (error) {
      console.error('Error fetching market price:', error);
    } finally {
      setIsLoading(false);
    }
  }, [marketIndex]);

  // Initial fetch
  useEffect(() => {
    if (marketIndex) {
      refreshPrice();
    }
  }, [marketIndex, refreshPrice]);

  // Set up periodic updates
  useEffect(() => {
    if (!marketIndex) {
      return;
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      refreshPrice();
    }, updateInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [marketIndex, updateInterval, refreshPrice]);

  return { price, isLoading, refreshPrice };
};

